import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Fingerprint,
  Plus,
  Trash2,
  Shield,
  Loader2,
  Smartphone,
  Key,
} from 'lucide-react';
import { passkeyAPI } from '@/lib/api';
import {
  browserSupportsWebAuthn,
  createCredential,
  describeWebAuthnError,
} from '@/lib/webauthn';

/**
 * Passkeys required before the second factor is enforced on an account.
 *
 * Two, not one: enforcement latches on at this count, and a single authenticator
 * means a lost or wiped device is an account lockout with no way back in except
 * the break-glass account. The intended pairing is one platform authenticator
 * (Touch ID / Windows Hello) plus one roaming security key.
 */
export const MINIMUM_PASSKEYS = 2;

export default function PasskeySettings({ onCredentialsChange }) {
  const [status, setStatus] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [registrationChallenge, setRegistrationChallenge] = useState(null);
  const registrationFetchedRef = useRef(false);
  const [deviceName, setDeviceName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingCredential, setDeletingCredential] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const [statusData, credsData] = await Promise.all([
        passkeyAPI.getStatus(),
        passkeyAPI.listCredentials(),
      ]);
      setStatus(statusData);
      setCredentials(credsData.credentials || []);
    } catch (err) {
      console.error('Failed to load passkey status:', err);
      toast.error('Failed to load passkey settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // EIP-571: fetch the challenge when the dialog opens, not when Register is
  // clicked. Awaiting a network round trip inside the click handler spends the
  // transient user activation, and iOS Safari then refuses
  // navigator.credentials.create() with NotAllowedError -- which surfaces as
  // "Passkey prompt was cancelled or timed out". Desktop Chrome is lenient
  // here, so this only ever failed on mobile.
  //
  // Safe to pre-fetch: /passkey/register/options ignores device_name (it is
  // only stored at verify time), so the challenge does not depend on anything
  // the user has yet to type. The ref guard matters because React.StrictMode
  // double-invokes effects in development and each call mints a new challenge.
  useEffect(() => {
    if (!registerDialogOpen) {
      registrationFetchedRef.current = false;
      setRegistrationChallenge(null);
      return;
    }
    if (registrationFetchedRef.current) return;
    registrationFetchedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const { options, challenge_id: challengeId } = await passkeyAPI.registerOptions(deviceName);
        if (!cancelled) setRegistrationChallenge({ options, challengeId });
      } catch (err) {
        console.error('Failed to prepare the passkey challenge:', err);
        if (!cancelled) registrationFetchedRef.current = false;
      }
    })();
    return () => { cancelled = true; };
    // deviceName is deliberately not a dependency: the server ignores it here,
    // and re-fetching on every keystroke would rebind the challenge each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerDialogOpen]);

  const handleRegisterPasskey = async () => {
    if (!browserSupportsWebAuthn()) {
      toast.error('Passkeys are not supported in this browser');
      return;
    }

    try {
      // No await before createCredential when the challenge is already in hand.
      const prepared = registrationChallenge
        || await (async () => {
          const { options, challenge_id: challengeId } = await passkeyAPI.registerOptions(deviceName);
          return { options, challengeId };
        })();
      const { options, challengeId } = prepared;
      setRegistering(true);

      const credential = await createCredential(options);

      await passkeyAPI.registerVerify(credential, deviceName, challengeId);

      toast.success('Passkey registered successfully!');
      setRegisterDialogOpen(false);
      setDeviceName('');
      await loadStatus();
      // The enrollment gate reads the credential count off the user payload in
      // localStorage, so without this the caller still believes you have the
      // old count and keeps blocking you after registering the last passkey.
      onCredentialsChange?.();
    } catch (err) {
      console.error('Passkey registration failed:', err);
      // Options are rebound on every call, so retrying with the stale
      // challenge would fail with a mismatch rather than the real reason.
      setRegistrationChallenge(null);
      registrationFetchedRef.current = false;
      toast.error(describeWebAuthnError(err));
    } finally {
      setRegistering(false);
    }
  };

  const handleDeleteCredential = async () => {
    if (!deletingCredential) return;

    setDeleting(true);
    try {
      await passkeyAPI.deleteCredential(deletingCredential.id);
      toast.success('Passkey removed');
      setDeleteDialogOpen(false);
      setDeletingCredential(null);
      await loadStatus();
      onCredentialsChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove passkey');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!status?.is_superadmin) {
    return null;
  }

  // Render-time check, not just the one inside the click handler. A superadmin
  // held here by the enrollment gate cannot leave until they enroll, so a
  // browser that cannot enroll needs to say so on screen and point at the way
  // out — otherwise the only feedback is a toast fired by a button that looks
  // like it should work, on a page they cannot navigate away from.
  if (!browserSupportsWebAuthn()) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-muted rounded-lg">
              <Fingerprint className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <CardTitle className="text-lg">Passkeys aren&apos;t available here</CardTitle>
              <CardDescription>
                This browser doesn&apos;t support passkeys, so enrollment can&apos;t be
                completed on it.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            Sign in again using a current version of Chrome, Safari, Edge or Firefox
            on a device with biometrics or a security key. Passkeys are registered
            per browser, so the one you enroll on is the one you&apos;ll sign in with.
          </p>
          <p>
            If you can&apos;t reach a supported browser, contact a platform
            administrator — recovery goes through the break-glass account rather
            than this page.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Fingerprint className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">Passkey Authentication</CardTitle>
                <CardDescription>
                  Secure passwordless login using biometrics or security keys
                </CardDescription>
              </div>
            </div>
            <Badge variant={status?.passkey_enabled ? 'default' : 'secondary'}>
              {status?.passkey_enabled ? 'Enabled' : 'Not Set Up'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Info */}
          <div className="bg-muted/50 rounded-lg p-4 flex items-start gap-3">
            <Shield className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p>
                Passkeys provide phishing-resistant authentication using your device's biometrics 
                (Face ID, Touch ID, Windows Hello) or a hardware security key.
              </p>
              {status?.passkey_enabled && (
                <p className="mt-2 text-primary font-medium">
                  When you log in, you'll be prompted to use your passkey instead of a password.
                </p>
              )}
            </div>
          </div>

          {/* Registered Passkeys */}
          {credentials.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Registered Passkeys</h4>
              <div className="space-y-2">
                {credentials.map((cred) => (
                  <div 
                    key={cred.id}
                    className="flex items-center justify-between p-3 bg-background border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-muted rounded-lg">
                        <Smartphone className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{cred.device_name || 'Passkey'}</p>
                        <p className="text-xs text-muted-foreground">
                          Added {new Date(cred.created_at).toLocaleDateString()}
                          {cred.last_used_at && (
                            <> · Last used {new Date(cred.last_used_at).toLocaleDateString()}</>
                          )}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => {
                        setDeletingCredential(cred);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Passkey Button */}
          <Button
            onClick={() => setRegisterDialogOpen(true)}
            className="w-full"
            variant={credentials.length > 0 ? 'outline' : 'default'}
          >
            <Plus className="w-4 h-4 mr-2" />
            {credentials.length > 0 ? 'Add Another Passkey' : 'Set Up Passkey'}
          </Button>
        </CardContent>
      </Card>

      {/* Register Dialog */}
      <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Register Passkey
            </DialogTitle>
            <DialogDescription>
              Create a passkey to sign in securely without a password.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Device Name (optional)</label>
              <Input
                placeholder="e.g., MacBook Pro, iPhone 15"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Give this passkey a name to identify it later
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-2">What happens next:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Your browser will prompt for biometric verification</li>
                <li>Use Face ID, Touch ID, or your security key</li>
                <li>The passkey will be stored securely on your device</li>
              </ol>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRegisterDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRegisterPasskey} disabled={registering}>
              {registering ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Fingerprint className="w-4 h-4 mr-2" />
                  Create Passkey
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Passkey?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove "{deletingCredential?.device_name || 'this passkey'}" from your account.
              {credentials.length === 1 && (
                <span className="block mt-2 text-warning">
                  This is your only passkey. You'll need to use password login after removing it.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCredential}
              className="bg-destructive hover:bg-destructive/90"
              disabled={deleting}
            >
              {deleting ? 'Removing...' : 'Remove Passkey'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
